import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Mail } from "lucide-react";
import NoAccessPage from "@/pages/NoAccess";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * If provided, the user must hold this exact permission string.
   * Superadmins bypass this check.
   * For multi-permission any-of gating, prefer the `permissions` prop.
   */
  permission?: string;
  /**
   * If provided, the user must hold AT LEAST ONE of these permissions.
   * Superadmins bypass this check. Permissions are sourced from the
   * decoded JWT access token (see AuthContext.permissions).
   *
   * Prefer this over `roles` for new routes — permissions match the
   * backend RBAC layer exactly, so the frontend gate cannot drift from
   * the API's actual authorization rules.
   */
  permissions?: string[];
  /**
   * If provided, at least one of these roles must be present on the user.
   * Kept for backward compatibility — new routes should use `permissions`.
   */
  roles?: string[];
  /**
   * If true, only superadmins are allowed. All other authenticated users are redirected.
   */
  superadminOnly?: boolean;
  /**
   * Where to redirect unauthenticated users. Defaults to /login.
   */
  redirectTo?: string;
  /**
   * Custom fallback rendered when user is authenticated but lacks access.
   * Defaults to rendering the NoAccess (403) page with the original URL preserved.
   */
  unauthorizedFallback?: ReactNode;
}

// ---------------------------------------------------------------------------
// No employee record screen
// ---------------------------------------------------------------------------
function NoEmployeeRecordScreen({ email }: { email: string }) {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 p-8 text-center shadow-sm">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-zinc-100 mx-auto mb-4">
          <Mail className="w-6 h-6 text-zinc-500" />
        </div>
        <h1 className="text-lg font-semibold text-zinc-900 mb-2">
          No employee record found
        </h1>
        <p className="text-sm text-zinc-500 mb-1">
          Your account <span className="font-medium text-zinc-700">{email}</span> is not linked to an employee profile.
        </p>
        <p className="text-sm text-zinc-500 mb-6">
          Please ask your HR team to create your employee record before you can access the system.
        </p>
        <button
          onClick={logout}
          className="text-sm text-zinc-500 hover:text-zinc-700 underline underline-offset-2 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading spinner (inline — avoids shadcn dep here)
// ---------------------------------------------------------------------------
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div
        className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProtectedRoute
// ---------------------------------------------------------------------------

export default function ProtectedRoute({
  children,
  permission,
  permissions,
  roles,
  superadminOnly,
  redirectTo = "/login",
  unauthorizedFallback,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated, hasRole, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();

  // Still bootstrapping auth state — show spinner
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Not authenticated — redirect to login, preserving intended destination
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Block users with no linked employee record. The "no employee record"
  // screen is keyed on permanent identity (is_superadmin) — a downscoped
  // superadmin still has a User row and shouldn't see that screen.
  if (!user.is_superadmin && !user.employee_id) {
    return <NoEmployeeRecordScreen email={user.email} />;
  }

  // Multi-role users who haven't picked a role yet must select one before
  // entering any protected page. Counts is_superadmin as one option.
  const optionCount =
    (user.is_superadmin ? 1 : 0) +
    user.effective_roles.filter((r) => r !== 'superadmin').length;
  if (user.active_role === null && optionCount > 1) {
    return (
      <Navigate
        to="/select-role"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Helper to render the 403 page (or a custom fallback) consistently.
  const renderDenied = () =>
    unauthorizedFallback !== undefined ? <>{unauthorizedFallback}</> : <NoAccessPage />;

  // Superadmin-only routes mirror backend enforcement, which follows the JWT.
  // Use currently_superadmin so a downscoped superadmin sees NoAccess (matching
  // what the API will return) instead of being silently let through the gate.
  if (superadminOnly && !user.currently_superadmin) {
    return renderDenied();
  }

  // Gating rules:
  //   - If BOTH `permissions` and `roles` are provided, pass when EITHER
  //     check matches. Use this for routes whose backend allows access
  //     via either a permission flag (HR / finance) OR a role (manager).
  //     Example: /manager — backend requires the "manager" role; frontend
  //     used to require "leave.approve" perm, which locked out managers
  //     who hadn't been granted the perm.
  //   - Otherwise, fall back to the historical precedence: permissions
  //     → permission → roles.
  //   hasPermission / hasAnyPermission already key superadmin bypass on
  //   currently_superadmin (see AuthContext).
  const hasPermissionsList = !!(permissions && permissions.length > 0);
  const hasRolesList = !!(roles && roles.length > 0);

  if (hasPermissionsList && hasRolesList) {
    const permPass = hasAnyPermission(permissions!);
    const rolePass =
      user.currently_superadmin || roles!.some((r) => hasRole(r));
    if (!permPass && !rolePass) {
      return renderDenied();
    }
  } else if (hasPermissionsList) {
    if (!hasAnyPermission(permissions!)) {
      return renderDenied();
    }
  } else if (permission) {
    if (!hasPermission(permission)) {
      return renderDenied();
    }
  } else if (hasRolesList) {
    const hasRequiredRole =
      user.currently_superadmin || roles!.some((r) => hasRole(r));
    if (!hasRequiredRole) {
      return renderDenied();
    }
  }

  // No constraints provided beyond authentication — allow.
  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// PermissionGate — inline guard for partial-page sections
// ---------------------------------------------------------------------------

interface PermissionGateProps {
  children: ReactNode;
  roles?: string[];
  fallback?: ReactNode;
}

/**
 * Render children only if the user holds one of the required roles.
 * Use this for showing/hiding UI elements rather than full route protection.
 *
 * Example:
 *   <PermissionGate roles={["hr_admin", "hr_country"]}>
 *     <RunPayrollButton />
 *   </PermissionGate>
 */
export function PermissionGate({
  children,
  roles = [],
  fallback = null,
}: PermissionGateProps) {
  const { user, isLoading, hasRole } = useAuth();

  if (isLoading || !user) return <>{fallback}</>;

  if (user.currently_superadmin) return <>{children}</>;

  const allowed = roles.length === 0 || roles.some((r) => hasRole(r));
  return allowed ? <>{children}</> : <>{fallback}</>;
}
